import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Gem, Star, User, BadgeCheck, Briefcase, Users, UsersRound, Landmark, Globe2, UserPlus, CreditCard, ShieldCheck, Settings, TrendingUp, Handshake, Headphones, ArrowRight, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/c8cb156ce4197ef54b5c43570c74170022b926404b3afb046482675639642a27.jpeg";

const TIER_THEMES = {
  gold: { text: "text-gold", border: "border-gold/60", btn: "bg-gold text-navy-950 hover:bg-gold-light", hex: "border-gold text-gold", glow: "shadow-[0_0_50px_-12px_rgba(212,175,55,0.35)]" },
  diamond: { text: "text-techblue-light", border: "border-techblue/70", btn: "bg-techblue text-white hover:bg-techblue-light", hex: "border-techblue-light text-techblue-light", glow: "shadow-[0_0_60px_-10px_rgba(96,165,250,0.4)]" },
  platinum: { text: "text-slate-200", border: "border-slate-400/60", btn: "bg-slate-200 text-navy-950 hover:bg-white", hex: "border-slate-300 text-slate-200", glow: "shadow-[0_0_50px_-12px_rgba(148,163,184,0.3)]" },
};

const TIERS = [
  {
    key: "gold", name: "GOLD", sub: "MEMBERSHIP", icon: Crown, price: "₹9,999", period: "/year", pkg: "essential",
    features: [
      [User, "Business Network", "Access to global business network"],
      [BadgeCheck, "Member Profile", "Premium business profile"],
      [Briefcase, "Business Opportunities", "Access to curated opportunities"],
      [Users, "Networking Access", "Connect with professionals"],
    ],
  },
  {
    key: "diamond", name: "DIAMOND", sub: "MEMBERSHIP", icon: Gem, price: "₹79,999", period: "/year", pkg: "business", includes: "EVERYTHING IN GOLD", featured: true,
    features: [
      [UsersRound, "Priority Opportunities", "Get early access to priority deals"],
      [Landmark, "Capital & Investor Connections", "Connect with investors & lenders"],
      [Handshake, "Strategic Business Access", "Access to strategic partnerships"],
    ],
  },
  {
    key: "platinum", name: "PLATINUM", sub: "MEMBERSHIP", icon: Star, price: "Custom", period: "", pkg: null, includes: "EVERYTHING IN DIAMOND",
    features: [
      [Crown, "Premium Deal Access", "Exclusive high-value opportunities"],
      [UsersRound, "Strategic Advisory", "Expert guidance for your growth"],
      [Landmark, "Priority Capital Connections", "Fast-track funding connections"],
      [Globe2, "Exclusive Global Network", "Invitation-only global network"],
    ],
  },
];

const ACTIVATION_STEPS = [
  { n: "01", icon: UserPlus, color: "text-gold border-gold/60", title: "SELECT CATEGORY", desc: "Choose Gold, Diamond or Platinum" },
  { n: "02", icon: CreditCard, color: "text-techblue-light border-techblue/60", title: "MAKE PAYMENT", desc: "Secure payment via trusted gateway" },
  { n: "03", icon: ShieldCheck, color: "text-emerald-400 border-emerald-500/60", title: "PAYMENT VERIFIED", desc: "Payment successful & verified" },
  { n: "04", icon: Settings, color: "text-violet-300 border-violet-400/60", title: "ADMIN APPROVAL", desc: "Account reviewed by admin" },
  { n: "05", icon: BadgeCheck, color: "text-gold border-gold/60", title: "ACTIVE MEMBER", desc: "Account activated & enjoy member benefits" },
];

const TRUST_ROW = [
  [Users, "TRUSTED NETWORK", "Connect with verified business leaders"],
  [TrendingUp, "GROW YOUR BUSINESS", "Access opportunities that drive growth"],
  [Handshake, "CAPITAL ACCESS", "Connect with lenders, investors & partners"],
  [Headphones, "EXPERT SUPPORT", "Get advisory & support at every step"],
];

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const PKG_TO_PLAN = { essential: "ESSENTIAL", business: "BUSINESS" };

export default function Membership() {
  const [loadingTier, setLoadingTier] = useState("");
  const [planPrices, setPlanPrices] = useState({});
  useEffect(() => {
    api.get("/membership/plans").then((r) => setPlanPrices(Object.fromEntries(r.data.map((p) => [p.name, p.price])))).catch(() => {});
  }, []);
  const planPrice = (t) => {
    const name = PKG_TO_PLAN[t.pkg] || (t.key === "platinum" ? "ENTERPRISE" : null);
    return (name && planPrices[name]) || t.price;
  };
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("canceled")) toast.info("Checkout canceled — you can restart anytime.");
  }, [params]);

  const payWithStripe = async (pkg) => {
    const { data } = await api.post("/payments/checkout", { package_id: pkg, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const select = async (tier) => {
    if (!tier.pkg) { navigate("/contact?topic=Platinum Membership (Custom)"); return; }
    setLoadingTier(tier.key);
    try {
      const { data: order } = await api.post("/razorpay/create-order", { package_id: tier.pkg });
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("script");
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "10XGLOBAL",
        description: `${tier.name} Membership (Annual)`,
        order_id: order.order_id,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/razorpay/verify", { order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature });
            window.location.href = `/payment/success?razorpay_order=${resp.razorpay_order_id}`;
          } catch (e) {
            toast.error("Payment verification failed — contact support if the amount was debited.");
          }
        },
        modal: { ondismiss: () => toast.info("Payment window closed — no charge made.") },
      });
      rz.open();
    } catch (e) {
      if (e.response?.status === 503) {
        toast.info("Razorpay setup pending — completing securely with Stripe instead.");
        try { await payWithStripe(tier.pkg); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment.");
      }
    } finally {
      setLoadingTier("");
    }
  };

  return (
    <div data-testid="membership-page">
      {/* HEADER */}
      <section className="relative overflow-hidden border-b border-slate-800" data-testid="membership-hero">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/80 to-navy-950" />
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-12 text-center">
          <p className="font-display text-3xl sm:text-4xl font-black tracking-tight"><span className="text-gold">10X</span><span className="text-slate-100">GLOBAL</span></p>
          <p className="font-mono text-[9px] tracking-[0.5em] text-slate-400 mt-1">A D V I S O R Y</p>
          <p className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-slate-300 mt-5">FUNDING BEYOND EXPECTATIONS</p>
          <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.05]">
            <span className="block text-slate-100 text-xl sm:text-2xl lg:text-3xl">CHOOSE YOUR</span>
            <span className="block text-gold text-4xl sm:text-5xl lg:text-6xl mt-2">MEMBERSHIP CATEGORY</span>
          </h1>
          <p className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-slate-400 mt-6">CONNECT &nbsp;·&nbsp; COLLABORATE &nbsp;·&nbsp; GROW</p>
        </div>
      </section>

      {/* TIERS */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16" data-testid="membership-tiers">
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t) => {
            const theme = TIER_THEMES[t.key];
            return (
              <div key={t.key} data-testid={`membership-tier-${t.key}`}
                className={`card-surface ${theme.border} ${theme.glow} p-7 flex flex-col ${t.featured ? "lg:-my-4 lg:py-11" : ""}`}>
                <div className="flex justify-center">
                  <div className={`w-20 h-20 border-2 ${theme.hex} flex items-center justify-center`} style={{ clipPath: "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)" }}>
                    <t.icon size={30} strokeWidth={1.5} />
                  </div>
                </div>
                <p className={`text-center font-display font-black text-2xl tracking-wide mt-5 ${theme.text}`}>{t.name}</p>
                <p className="text-center text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400 mt-1">{t.sub}</p>
                <p className="text-center mt-3">
                  <span className="font-display font-bold text-xl text-slate-100">{planPrice(t)}</span>
                  <span className="text-xs text-slate-500">{t.period}</span>
                </p>
                {t.includes && <p className={`text-center text-[9px] font-mono uppercase tracking-[0.25em] mt-3 ${theme.text}`}>— {t.includes} —</p>}
                <ul className="mt-7 space-y-5 flex-1">
                  {t.features.map(([Icon, title, desc]) => (
                    <li key={title} className="flex items-start gap-3.5">
                      <span className={`w-9 h-9 rounded-full border ${theme.hex} flex items-center justify-center shrink-0`}><Icon size={15} /></span>
                      <div>
                        <p className="text-sm font-semibold text-slate-100">{title}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button onClick={() => select(t)} disabled={!!loadingTier} data-testid={`select-${t.key}-btn`}
                  className={`${theme.btn} mt-8 w-full font-bold text-sm tracking-wide py-3.5 rounded-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2`}>
                  {loadingTier === t.key ? <><Loader2 size={15} className="animate-spin" /> OPENING SECURE PAYMENT…</> : `SELECT ${t.name}`}
                </button>
                {t.pkg && (
                  <button onClick={async () => { setLoadingTier(t.key); try { await payWithStripe(t.pkg); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setLoadingTier(""); } }}
                    data-testid={`stripe-${t.key}-btn`} className="mt-2.5 w-full text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
                    or pay by card with Stripe →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ACTIVATION PROCESS */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="activation-process">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="text-center font-mono text-sm md:text-base tracking-[0.3em] text-gold mb-12">— SIMPLE MEMBERSHIP ACTIVATION PROCESS —</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {ACTIVATION_STEPS.map((s, i) => (
              <div key={s.n} className="relative text-center" data-testid={`activation-step-${s.n}`}>
                <span className={`inline-flex w-16 h-16 rounded-full border-2 ${s.color} bg-navy-950 items-center justify-center`}><s.icon size={26} strokeWidth={1.5} /></span>
                <p className="font-mono text-xs text-slate-500 mt-3">{s.n}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-100 mt-1.5 leading-snug">{s.title}</p>
                <p className="text-[10px] text-slate-500 mt-1.5 leading-snug max-w-[180px] mx-auto">{s.desc}</p>
                {i < ACTIVATION_STEPS.length - 1 && <ArrowRight size={16} className="hidden md:block absolute -right-5 top-7 text-slate-600" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST ROW */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14" data-testid="membership-trust-row">
        <div className="card-surface p-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST_ROW.map(([Icon, title, desc]) => (
            <div key={title} className="flex items-start gap-3.5">
              <Icon size={26} className="text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-100">{title}</p>
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP CERTIFICATE PREVIEW */}
      <section className="border-t border-slate-800" data-testid="membership-certificate-preview">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 text-center">
          <p className="gold-kicker mb-3">Trust, Credibility & Visibility</p>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-100 tracking-tight" data-testid="certificate-preview-title">YOUR OFFICIAL MEMBERSHIP CERTIFICATE</h2>
          <p className="text-sm text-slate-400 mt-3 max-w-xl mx-auto">Every approved member receives an official 10XGLOBAL membership certificate — with your company name, member ID, validity and verification QR.</p>
          <div className="relative overflow-hidden rounded-sm mt-10" style={{ border: "2px solid #D4AF37", containerType: "inline-size" }} data-testid="membership-certificate-sample">
            <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_b2b-intelligence-2/artifacts/jj6ssbp9_IMG_20260831_182211.png" alt="10XGLOBAL Certificate of Membership — sample" className="w-full block" data-testid="certificate-sample-art" />
            <div className="absolute flex items-center justify-center" style={{ left: "10.5%", top: "47.5%", width: "52%", height: "14.5%", background: "#040A1A", borderBottom: "1px solid rgba(212,175,55,0.35)" }}>
              <p className="font-display italic font-bold bg-gradient-to-b from-[#F9E9B8] via-[#D4AF37] to-[#B08A2E] bg-clip-text text-transparent truncate px-3" style={{ fontSize: "4.8cqw", lineHeight: 1.2 }} data-testid="certificate-sample-name">Your Company Name</p>
            </div>
            <div className="absolute flex items-center justify-center" style={{ left: "21%", top: "82.3%", width: "28.5%", height: "6.5%", background: "#060D20" }}>
              <p className="font-mono font-bold text-white tracking-wider" style={{ fontSize: "2.5cqw" }} data-testid="certificate-sample-id">10XG-XXXXXX</p>
            </div>
            <p className="absolute text-center font-mono text-slate-300/80" style={{ left: "19%", top: "93.6%", width: "33%", fontSize: "1.6cqw" }} data-testid="certificate-sample-valid">VALID TILL DD/MM/YYYY</p>
            <div className="absolute flex flex-col items-center gap-1" style={{ left: "3.5%", bottom: "3%", width: "9.5%" }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent("https://10x-global.com/membership")}`}
                alt="Scan to verify membership" className="w-full border border-[#D4AF37]/70 rounded-sm bg-white p-0.5" data-testid="certificate-sample-qr" />
              <p className="font-mono text-[#D4AF37] tracking-widest" style={{ fontSize: "1.5cqw" }}>SCAN TO VERIFY</p>
            </div>
          </div>
          <p className="text-[11px] text-slate-600 mt-4 font-mono">Sample — your certificate is generated with your company details after membership approval.</p>
        </div>
      </section>

      {/* BOTTOM STRIP */}
      <section className="border-t border-slate-800 bg-navy-900/40" data-testid="membership-bottom-strip">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-5">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 border-2 border-techblue-light text-techblue-light font-display font-black text-sm flex items-center justify-center" style={{ clipPath: "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)" }}>10X</span>
            <p className="font-display font-bold text-slate-100 text-sm leading-snug">ONE NETWORK.<br /><span className="text-gold">ENDLESS OPPORTUNITIES.</span></p>
          </div>
          <div className="text-center"><p className="font-mono text-2xl font-semibold text-slate-100">500+</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Business Opportunities</p></div>
          <div className="text-center"><p className="font-mono text-2xl font-semibold text-slate-100">100+</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Investor Connections</p></div>
          <div className="text-center">
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Grow With</p>
            <p className="font-display font-black text-lg mt-1"><span className="text-gold">10X</span><span className="text-slate-100">GLOBAL</span></p>
          </div>
          <p className="text-xs font-mono text-slate-400 flex items-center gap-2"><Globe2 size={14} className="text-gold" /> www.10x-global.com</p>
        </div>
      </section>
    </div>
  );
}
